import { CAR_PRESETS, DEFAULT_UPGRADES, type CarUpgrade, type VehicleBlueprint } from "@neon-arena/shared";
import type { CompArena } from "../testlab/ComparisonLab.js";

const PRESET_KEYS = ["stock", "balanced", "speed", "grip", "drift", "combat", "lightweight"];
const UPGRADE_KEYS: Array<keyof CarUpgrade> = ["engine", "gearbox", "turbo", "suspension", "aero", "brakes", "boost"];

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === "&" ? "&amp;" : c === "<" ? "&lt;" : c === ">" ? "&gt;" : c === '"' ? "&quot;" : "&#39;",
  );
}

export interface LabPickerCallbacks {
  onStartDemo: (blueprints: VehicleBlueprint[], presets: string[]) => void;
  onStartComparison: (bpA: VehicleBlueprint, upgradesA: CarUpgrade, bpB: VehicleBlueprint, upgradesB: CarUpgrade, arena: CompArena) => void;
  onExit: () => void;
}

export class LabPicker {
  private el: HTMLDivElement;
  private blueprints: VehicleBlueprint[] = [];
  private upgradesA: CarUpgrade = { ...DEFAULT_UPGRADES };
  private upgradesB: CarUpgrade = { ...DEFAULT_UPGRADES };

  constructor(
    overlayEl: HTMLDivElement,
    private callbacks: LabPickerCallbacks,
  ) {
    this.el = overlayEl;
  }

  setBlueprints(bps: VehicleBlueprint[]): void {
    this.blueprints = bps;
  }

  show(): void {
    this.upgradesA = { ...DEFAULT_UPGRADES };
    this.upgradesB = { ...DEFAULT_UPGRADES };
    this.el.innerHTML = this.buildHTML();
    this.el.classList.remove("hidden");
    this.wire();
  }

  hide(): void {
    this.el.classList.add("hidden");
    this.el.innerHTML = "";
  }

  private buildHTML(): string {
    const bpOptions = this.blueprints.map((bp) =>
      `<option value="${esc(bp.id)}">${esc(bp.name)}</option>`
    ).join("");
    const presetOptions = PRESET_KEYS.map((k) =>
      `<option value="${k}">${k}</option>`
    ).join("");

    const demoVehicleRows = this.blueprints.map((bp, i) => `
      <label class="lp-vehicle-row">
        <input type="checkbox" class="lp-demo-check" data-id="${esc(bp.id)}" ${i < 2 ? "checked" : ""} />
        <span class="lp-vehicle-name">${esc(bp.name)}</span>
        <select class="lp-demo-preset" data-id="${esc(bp.id)}">
          ${presetOptions}
        </select>
      </label>
    `).join("");

    return `
      <div class="lp-panel">
        <div class="lp-title">VEHICLE TEST LAB</div>

        <div class="lp-cols">
          <div class="lp-col">
            <div class="lp-col-title">DEMO MODE</div>
            <div class="lp-col-desc">Automated test sequence — speed, cornering, drift, boost, combat. Up to 5 vehicles.</div>
            <div class="lp-demo-list">${demoVehicleRows}</div>
            <button class="lp-start-btn" id="lp-demo-start">START DEMO</button>
          </div>

          <div class="lp-col lp-col-right">
            <div class="lp-col-title">COMPARISON MODE</div>
            <div class="lp-col-desc">Drive two vehicles simultaneously with mirrored input. Compare handling live.</div>

            <div class="lp-comp-row">
              ${this.buildCompSide("a", bpOptions, presetOptions, 0)}
              <div class="lp-comp-vs">VS</div>
              ${this.buildCompSide("b", bpOptions, presetOptions, 1)}
            </div>

            <div class="lp-arena-label">ARENA</div>
            <div class="lp-arena-row">
              <button class="lp-arena-btn selected" data-arena="drag">DRAG STRIP</button>
              <button class="lp-arena-btn" data-arena="skidpan">SKIDPAN</button>
              <button class="lp-arena-btn" data-arena="weapons">WEAPONS</button>
            </div>

            <button class="lp-start-btn lp-comp-start-btn" id="lp-comp-start">START COMPARISON</button>
          </div>
        </div>

        <button class="lp-exit-btn" id="lp-exit">EXIT TO LOBBY</button>
      </div>
    `;
  }

  private buildCompSide(side: "a" | "b", bpOptions: string, presetOptions: string, defaultBpIdx: number): string {
    const label = side === "a" ? "VEHICLE A" : "VEHICLE B";
    const ucRows = UPGRADE_KEYS.map((key) =>
      `<div class="lp-uc-row">
        <span class="lp-uc-label">${key.slice(0, 6).toUpperCase()}</span>
        <button class="lp-uc-btn" data-side="${side}" data-key="${key}" data-delta="-1">−</button>
        <span class="lp-uc-val" id="lp-uc-${side}-${key}">0</span>
        <button class="lp-uc-btn" data-side="${side}" data-key="${key}" data-delta="1">+</button>
      </div>`
    ).join("");

    // Default second blueprint for side B
    const bpOptsForSide = defaultBpIdx === 0
      ? bpOptions
      : this.blueprints.map((bp, i) =>
          `<option value="${esc(bp.id)}"${i === defaultBpIdx ? " selected" : ""}>${esc(bp.name)}</option>`
        ).join("");

    return `<div class="lp-comp-side lp-comp-${side}">
      <div class="lp-comp-side-label">${label}</div>
      <select id="lp-comp-${side}-bp">${bpOptsForSide}</select>
      <select class="lp-comp-preset-sel" data-side="${side}" id="lp-comp-${side}-preset">
        ${presetOptions}
      </select>
      <div class="lp-uc-grid">${ucRows}</div>
    </div>`;
  }

  private wire(): void {
    // Arena toggle
    this.el.querySelectorAll<HTMLButtonElement>(".lp-arena-btn").forEach((btn) => {
      btn.addEventListener("click", () => {
        this.el.querySelectorAll(".lp-arena-btn").forEach((b) => b.classList.remove("selected"));
        btn.classList.add("selected");
      });
    });

    // Preset quick-fill for comparison sides
    this.el.querySelectorAll<HTMLSelectElement>(".lp-comp-preset-sel").forEach((sel) => {
      sel.addEventListener("change", () => {
        const side = sel.dataset["side"] as "a" | "b";
        const upgrades = { ...(CAR_PRESETS[sel.value] ?? DEFAULT_UPGRADES) };
        if (side === "a") this.upgradesA = upgrades;
        else this.upgradesB = upgrades;
        this.refreshCompSideUI(side);
      });
    });

    // Upgrade steppers for comparison sides
    this.el.querySelectorAll<HTMLButtonElement>(".lp-uc-btn").forEach((btn) => {
      btn.addEventListener("click", () => {
        const side = btn.dataset["side"] as "a" | "b";
        const key = btn.dataset["key"] as keyof CarUpgrade;
        const delta = parseInt(btn.dataset["delta"] ?? "0");
        const upgrades = side === "a" ? this.upgradesA : this.upgradesB;
        upgrades[key] = Math.max(0, Math.min(3, upgrades[key] + delta)) as 0 | 1 | 2 | 3;
        this.refreshCompSideUI(side);
      });
    });

    // Demo start
    this.el.querySelector("#lp-demo-start")?.addEventListener("click", () => {
      const checked = Array.from(this.el.querySelectorAll<HTMLInputElement>(".lp-demo-check:checked"));
      if (checked.length === 0) return;
      const bps: VehicleBlueprint[] = [];
      const presets: string[] = [];
      for (const cb of checked.slice(0, 5)) {
        const id = cb.dataset["id"] ?? "";
        const bp = this.blueprints.find((b) => b.id === id);
        if (!bp) continue;
        const sel = this.el.querySelector<HTMLSelectElement>(`.lp-demo-preset[data-id="${id}"]`);
        bps.push(bp);
        presets.push(sel?.value ?? "stock");
      }
      this.hide();
      this.callbacks.onStartDemo(bps, presets);
    });

    // Comparison start
    this.el.querySelector("#lp-comp-start")?.addEventListener("click", () => {
      const aId = this.el.querySelector<HTMLSelectElement>("#lp-comp-a-bp")?.value ?? "";
      const bId = this.el.querySelector<HTMLSelectElement>("#lp-comp-b-bp")?.value ?? "";
      const arenaBtn = this.el.querySelector<HTMLButtonElement>(".lp-arena-btn.selected");
      const arena = (arenaBtn?.dataset["arena"] ?? "drag") as CompArena;
      const bpA = this.blueprints.find((b) => b.id === aId);
      const bpB = this.blueprints.find((b) => b.id === bId);
      if (!bpA || !bpB) return;
      this.hide();
      this.callbacks.onStartComparison(bpA, { ...this.upgradesA }, bpB, { ...this.upgradesB }, arena);
    });

    // Exit
    this.el.querySelector("#lp-exit")?.addEventListener("click", () => {
      this.hide();
      this.callbacks.onExit();
    });
  }

  private refreshCompSideUI(side: "a" | "b"): void {
    const upgrades = side === "a" ? this.upgradesA : this.upgradesB;
    for (const key of UPGRADE_KEYS) {
      const valEl = document.getElementById(`lp-uc-${side}-${key}`);
      if (valEl) valEl.textContent = String(upgrades[key]);

      const btns = this.el.querySelectorAll<HTMLButtonElement>(`.lp-uc-btn[data-side="${side}"][data-key="${key}"]`);
      btns.forEach((btn) => {
        const delta = parseInt(btn.dataset["delta"] ?? "0");
        if (delta < 0) btn.disabled = upgrades[key] <= 0;
        if (delta > 0) btn.disabled = upgrades[key] >= 3;
      });
    }
  }
}
